import React, { useState, useEffect, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FaPlay, FaPause } from 'react-icons/fa';
import { getSongById } from '../utils/songUtils';
import { getSignedUrl } from '../utils/getSignedUrl';
import { useSongContext } from '../SongContext';
import { generateWaveformData } from '../utils/audioWaveform';

// Styled Components
const EmbedContainer = styled.div`
  margin: 24px 0;
  width: 100%;
  display: flex;
  justify-content: center;
`;

const CardWrapper = styled.div`
  width: 300px;
  
  @media (max-width: 768px) {
    width: 250px;
  }
`;

const CardLink = styled(Link)`
  display: block;
  width: 100%;
  aspect-ratio: 1;
  position: relative;
  border-radius: 15px;
  overflow: hidden;
  background: #2E2E2E;
  color: white;
  text-decoration: none;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  transition: background-image 0.3s ease-in-out;
`;

const ImageContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: ${props => props.$blurDataUrl ? `url(${props.$blurDataUrl})` : 'none'};
  background-size: cover;
  background-position: center;
  filter: ${props => props.$isLoading ? 'blur(10px)' : 'none'};
  transition: filter 0.3s ease-out;
`;

const MainImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: ${props => props.$bgImage ? `url(${props.$bgImage})` : 'none'};
  background-size: cover;
  background-position: center;
  opacity: ${props => props.$isLoaded ? 1 : 0};
  transition: opacity 0.3s ease-in-out;
`;

const TitlePill = styled.div`
  position: absolute;
  top: 15px;
  left: 15px;
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 8px;
  color: white;
  font-family: 'Rubik', sans-serif;
  font-size: 14px;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(4px);
  z-index: 2;
  max-width: calc(100% - 30px);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PlayerContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 15px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.4) 60%, transparent 100%);
  backdrop-filter: blur(10px);
  border-bottom-left-radius: 15px;
  border-bottom-right-radius: 15px;
`;

const PlayButton = styled.button`
  background: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #2e2e2e;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);

  &:hover {
    transform: scale(1.1);
  }

  svg {
    font-size: 14px;
  }
`;

const ProgressBar = styled.div`
  flex: 1;
  height: 60px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  transform: translateZ(0);
  will-change: transform;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  perspective: 1000;
  -webkit-perspective: 1000;
`;

const ProgressTracker = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  transform: translateZ(0);
  will-change: transform;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  perspective: 1000;
  -webkit-perspective: 1000;
`;

const ProgressFill = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: ${props => props.$progress}%;
  background: rgba(255, 255, 255, 0.2);
  transition: all 0.5s cubic-bezier(0.4, 0.0, 0.2, 1);
  transform-origin: left center;
  will-change: width, transform;
  transform: translateZ(0);
  backface-visibility: hidden;
  perspective: 1000;
  -webkit-backface-visibility: hidden;
  -webkit-perspective: 1000;
  -webkit-transform: translateZ(0);
`;

const WaveformBars = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 10px;
`;

const WaveBar = styled.div`
  width: 2px;
  height: ${props => props.$height * 1.5 || 20}%;
  background: ${props => props.$active ? 
    'linear-gradient(to bottom, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 1) 100%)' : 
    'linear-gradient(to bottom, rgba(255, 255, 255, 0.7) 0%, rgba(255, 255, 255, 0.3) 100%)'};
  border-radius: 1px;
  transition: 
    height 1.2s cubic-bezier(0.34, 1.56, 0.64, 1),
    transform 1.2s cubic-bezier(0.34, 1.56, 0.64, 1);
  transform-origin: center bottom;
  animation: ${props => props.$isReal ? 'none' : 'pulse 3s infinite ease-in-out'};
  transition-delay: ${props => props.$index * 25}ms;
  will-change: transform, height;

  &:hover {
    transform: scaleY(1.1);
  }

  @media (max-width: 480px) {
    width: 1.5px;
  }

  @keyframes pulse {
    0%, 100% {
      transform: scaleY(1);
    }
    50% {
      transform: scaleY(1.03);
    }
  }
`;

const ErrorMessage = styled.div`
  padding: 16px;
  background-color: #f8d7da;
  color: #721c24;
  border-radius: 8px;
  margin: 20px 0;
  max-width: 500px;
`;

const LoadingContainer = styled.div`
  width: 100%;
  aspect-ratio: 1;
  position: relative;
  border-radius: 15px;
  overflow: hidden;
  background: #2E2E2E;
  color: white;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.03) 0%,
    rgba(255, 255, 255, 0.08) 50%,
    rgba(255, 255, 255, 0.03) 100%
  );
  animation: shimmer 1.5s infinite;
  
  @keyframes shimmer {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }
`;

const LoadingTitle = styled.div`
  position: absolute;
  top: 15px;
  left: 15px;
  width: 120px;
  height: 30px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
`;

const LoadingPlayer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 80px;
  padding: 20px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.4) 60%, transparent 100%);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  gap: 15px;
`;

const LoadingPlayButton = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
`;

const LoadingWaveform = styled.div`
  flex: 1;
  height: 60px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  display: flex;
  align-items: center;
  padding: 0 10px;
  gap: 4px;
`;

const LoadingBar = styled.div`
  flex: 1;
  height: ${props => 20 + Math.random() * 40}%;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 1px;
  animation: pulse 1.5s infinite ease-in-out;
  animation-delay: ${props => props.$index * 0.1}s;

  @keyframes pulse {
    0%, 100% {
      transform: scaleY(1);
    }
    50% {
      transform: scaleY(1.1);
    }
  }
`;

// Global queue for waveform generation
const waveformQueue = [];
let isProcessingQueue = false;
const MAX_CONCURRENT_WAVEFORMS = 3;
let activeWaveformCount = 0;

// Process waveform generation queue
const processWaveformQueue = async () => {
  if (isProcessingQueue || waveformQueue.length === 0 || activeWaveformCount >= MAX_CONCURRENT_WAVEFORMS) {
    return;
  }

  isProcessingQueue = true;

  while (waveformQueue.length > 0 && activeWaveformCount < MAX_CONCURRENT_WAVEFORMS) {
    const { signedUrl, setWaveformData, setIsLoading, markComplete } = waveformQueue[0];
    
    try {
      activeWaveformCount++;
      const waveform = await generateWaveformData(signedUrl);
      setWaveformData(waveform);
    } catch (error) {
      console.error('Error generating waveform:', error);
    } finally {
      activeWaveformCount--;
      setIsLoading(false);
      markComplete();
      waveformQueue.shift();
    }
  }

  isProcessingQueue = false;

  // If there are still items in the queue and we have capacity, continue processing
  if (waveformQueue.length > 0 && activeWaveformCount < MAX_CONCURRENT_WAVEFORMS) {
    setTimeout(processWaveformQueue, 0);
  }
};

/**
 * SongEmbed - Renders a song card with album art and waveform player
 */
const SongEmbed = ({ songId, inRow = false }) => {
  const [song, setSong] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [albumArtSrc, setAlbumArtSrc] = useState('');
  const [imageLoaded, setImageLoaded] = useState(false);
  const [blurDataUrl, setBlurDataUrl] = useState('');
  const [waveformData, setWaveformData] = useState(null);
  const [isWaveformQueued, setIsWaveformQueued] = useState(false);
  const [playStartTime, setPlayStartTime] = useState(null);
  const hasTrackedRef = useRef(false);
  const progressRef = useRef(null);
  const isTransitioning = useRef(false);
  const audioRef = useRef(null);

  const { 
    currentSongId,
    isPlaying: contextIsPlaying,
    currentProgress,
    loadAudioBuffer,
    playSong,
    pausePlayback,
    seekTo,
    songsLoaded
  } = useSongContext();

  const [isPlaying, setIsPlaying] = useState(false);

  // Generate a tiny blur placeholder
  const generateBlurPlaceholder = (width = 20) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = width;
    canvas.height = width;

    ctx.fillStyle = '#2E2E2E';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    return canvas.toDataURL('image/jpeg', 0.1);
  };

  // Track play function
  const trackPlay = async (songId, playDuration) => {
    console.log('Tracking play:', { songId, playDuration });
    
    try {
      const token = localStorage.getItem('token');
      const url = `${process.env.REACT_APP_API_BASE_URL}/songs/${songId}/play`;

      const headers = {
        'Content-Type': 'application/json'
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ playDuration })
      });

      if (!response.ok) {
        console.error('Play tracking failed:', {
          status: response.status,
          statusText: response.statusText
        });
        return;
      }

      await response.json();
      console.log('Play tracked successfully for song:', songId);
    } catch (error) {
      console.error('Error tracking play:', {
        error: error.message,
        songId,
        playDuration
      });
    }
  };

  // Fetch song data
  useEffect(() => {
    const fetchSong = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const songData = await getSongById(songId);
        if (songData) {
          setSong(songData);

          // Handle album art loading
          if (songData.song_art) {
            setBlurDataUrl(generateBlurPlaceholder());
            
            const artPath = songData.song_art.startsWith('/') 
              ? songData.song_art.slice(1) 
              : songData.song_art;
            
            const signedArtUrl = await getSignedUrl(artPath);
            
            if (signedArtUrl) {
              const img = new Image();
              img.onload = () => {
                setAlbumArtSrc(signedArtUrl);
                setImageLoaded(true);
              };
              img.src = signedArtUrl;
            }
          }
        } else {
          setError(`Could not find song with ID: ${songId}`);
        }
      } catch (err) {
        console.error('Error fetching song:', err);
        setError(`Error loading song: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    if (songId) {
      fetchSong();
    } else {
      setError('No song ID provided');
      setLoading(false);
    }
  }, [songId]);

  // Load waveform data when song is loaded
  useEffect(() => {
    const loadWaveform = async () => {
      if (!song?.song_file_wm || isWaveformQueued || waveformData) return;

      try {
        setIsWaveformQueued(true);

        const signedUrl = await getSignedUrl(song.song_file_wm);
        if (!signedUrl) {
          throw new Error('Failed to get signed URL');
        }

        waveformQueue.push({
          signedUrl,
          setWaveformData,
          setIsLoading: () => {},
          markComplete: () => setIsWaveformQueued(false)
        });

        processWaveformQueue();

      } catch (error) {
        console.error('Error queueing waveform generation:', error);
        setIsWaveformQueued(false);
      }
    };

    loadWaveform();
  }, [song?.song_file_wm, isWaveformQueued, waveformData]);

  // Use real waveform data if available, otherwise generate placeholder
  const displayWaveform = useMemo(() => {
    if (waveformData) {
      const minDisplayHeight = 2;
      const maxDisplayHeight = 65;
      const displayRange = maxDisplayHeight - minDisplayHeight;
      const originalMin = 20;
      const originalMax = 90;
      const originalRange = originalMax - originalMin;

      return {
        data: waveformData.map(value => {
          const clampedValue = Math.max(originalMin, Math.min(originalMax, value));
          let normalizedOriginal = 0;
          if (originalRange > 0) {
            normalizedOriginal = (clampedValue - originalMin) / originalRange;
          }
          return Math.round(minDisplayHeight + normalizedOriginal * displayRange);
        }),
        isReal: true
      };
    }
    
    const barsCount = 48;
    const bars = new Array(barsCount);
    for (let i = 0; i < barsCount; i++) {
      const baseHeight = Math.sin((i / barsCount) * Math.PI * 4) * 25 + 30;
      const randomVariation = Math.random() * 8 - 4;
      const height = Math.max(2, Math.min(65, baseHeight + randomVariation));
      bars[i] = Math.round(height);
    }
    
    for (let i = 1; i < barsCount - 1; i++) {
      bars[i] = Math.round((bars[i - 1] + bars[i] * 2 + bars[i + 1]) / 4);
    }
    
    return {
      data: bars,
      isReal: false
    };
  }, [waveformData]);

  // Calculate active bar index based on progress
  const activeBarIndex = useMemo(() => {
    if (!isPlaying || song?.song_id !== currentSongId) return -1;
    return Math.floor((displayWaveform.data.length * (currentProgress || 0)) / 100);
  }, [isPlaying, currentProgress, displayWaveform.data.length, song?.song_id, currentSongId]);

  // Handle play/pause toggle
  const handlePlayToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!song?.song_id || isTransitioning.current) return;
    
    try {
      isTransitioning.current = true;
      
      if (isPlaying) {
        pausePlayback();
        setIsPlaying(false);
        
        if (playStartTime && !hasTrackedRef.current) {
          const playDuration = Math.floor((Date.now() - playStartTime) / 1000);
          if (playDuration >= 5) {
            hasTrackedRef.current = true;
            trackPlay(song.song_id, playDuration);
          }
          setPlayStartTime(null);
        }
      } else {
        if (!songsLoaded[song.song_id]) {
          await loadAudioBuffer(song.song_id, song.song_file_wm);
        }
        
        const success = await playSong(song.song_id, song.song_file_wm, 0);
        
        if (success) {
          setIsPlaying(true);
          setPlayStartTime(Date.now());
          hasTrackedRef.current = false;
        }
      }
    } catch (error) {
      console.error('Error toggling playback:', error);
    } finally {
      isTransitioning.current = false;
    }
  };

  // Handle progress bar click
  const handleProgressClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!progressRef.current || !song?.song_id || isTransitioning.current) return;
    
    const rect = progressRef.current.getBoundingClientRect();
    const position = ((e.clientX - rect.left) / rect.width) * 100;
    
    try {
      isTransitioning.current = true;
      
      if (!isPlaying) {
        if (!songsLoaded[song.song_id]) {
          await loadAudioBuffer(song.song_id, song.song_file_wm);
        }
        const success = await playSong(song.song_id, song.song_file_wm, position);
        if (success) {
          setIsPlaying(true);
        }
      } else {
        seekTo(position);
      }
    } catch (error) {
      console.error('Error handling progress click:', error);
    } finally {
      isTransitioning.current = false;
    }
  };

  // Sync playing state with context
  useEffect(() => {
    if (song?.song_id === currentSongId) {
      setIsPlaying(contextIsPlaying);
    } else if (isPlaying) {
      setIsPlaying(false);
    }
  }, [currentSongId, contextIsPlaying, song?.song_id, isPlaying]);

  // Track plays when component unmounts or song changes
  useEffect(() => {
    return () => {
      if (isPlaying && playStartTime && !hasTrackedRef.current) {
        const playDuration = Math.floor((Date.now() - playStartTime) / 1000);
        if (playDuration >= 5) {
          hasTrackedRef.current = true;
          trackPlay(song?.song_id, playDuration);
        }
      }
    };
  }, [isPlaying, playStartTime, song?.song_id]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playing = isPlaying;
    }
  }, [isPlaying]);

  const renderContent = () => {
    if (loading) {
      return (
        <CardWrapper>
          <LoadingContainer>
            <LoadingOverlay />
            <LoadingTitle />
            <LoadingPlayer>
              <LoadingPlayButton />
              <LoadingWaveform>
                {Array.from({ length: 30 }).map((_, index) => (
                  <LoadingBar key={index} $index={index} />
                ))}
              </LoadingWaveform>
            </LoadingPlayer>
          </LoadingContainer>
        </CardWrapper>
      );
    }

    if (error) {
      return <ErrorMessage>{error}</ErrorMessage>;
    }

    if (!song) {
      return null;
    }

    return (
      <CardWrapper>
        <CardLink to={`/songs/${song.song_id}`}>
          <TitlePill>{song.song_title}</TitlePill>
          <ImageContainer $blurDataUrl={blurDataUrl} $isLoading={!imageLoaded} />
          <MainImage $bgImage={albumArtSrc} $isLoaded={imageLoaded} />
          <PlayerContainer>
            <PlayButton onClick={handlePlayToggle}>
              {isPlaying ? <FaPause /> : <FaPlay />}
            </PlayButton>
            <ProgressBar 
              ref={progressRef}
              onClick={handleProgressClick}
            >
              <ProgressTracker>
                <ProgressFill $progress={song?.song_id === currentSongId ? (currentProgress || 0) : 0} />
              </ProgressTracker>
              <WaveformBars>
                {displayWaveform.data.map((height, index) => (
                  <WaveBar 
                    key={index}
                    $height={height}
                    $index={index}
                    $isReal={displayWaveform.isReal}
                    $active={isPlaying && song?.song_id === currentSongId && index <= activeBarIndex}
                  />
                ))}
              </WaveformBars>
            </ProgressBar>
          </PlayerContainer>
        </CardLink>
      </CardWrapper>
    );
  };

  if (inRow) {
    return renderContent();
  }

  return (
    <EmbedContainer>
      {renderContent()}
    </EmbedContainer>
  );
};

export default SongEmbed; 